import { useMsal } from "@azure/msal-react";
import {
    getTheme, ICommandBarItemProps,
    IContextualMenuItem,
    Stack,
    Text,
    MessageBar,
    MessageBarType
} from "@fluentui/react";
import { useBoolean } from '@fluentui/react-hooks';
import { AddIcon } from '@fluentui/react-icons-mdl2';
import { RootState } from "app/store";
import AccountSummary from "components/AccountSummary";
import CustomPanel from "components/controls/CustomPanel";
import SimpleCard from "components/controls/SimpleCard";
import useHiddenWithMount from "components/useHiddenWithMount";
import saveAs from "file-saver";
import { AppLinkTo, AppNavigateToExternal, compareUnderWritingReviewToWithoutDispatch, emitCustomEvent, ENavigationReason, EViewType } from "global/Application";
import { Constants } from "global/Application/Constants";
import { useApplication } from "global/Application/Context";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateGroupConfiguration, addUpdateStopLoss, IAddFeesPayload } from "global/Reducer/dataSlice";
import { setProcessing } from "global/Reducer/statusSlice";
import { BuildVisibilityScope } from "global/Visibility/GlobalVisibility";
import { bcbs_groupconfigurationsnapshot } from "model/AttributeOptionSets.type";
import {
    EGroupConfigurationStatus as FAM_EGroupConfigurationStatus,
    IAccountsReceivable as FAM_IAccountsReceivable,
    IStopLoss as FAM_IStopLoss
} from "model/FlexibleAttributeModel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { RouterParams } from "../../App";
import SnapshotSummary from "../../components/SnapshotSummary";
import AccountsReceivableCard from "./AccountsReceivableCard";
import AccountsReceivableWizard from './AccountsReceivableWizard';
import AdobeCampaignExtract from "./AdobeCampaignExtract";
import BenefitDetailExtract from "./BenefitDetailExtract";
import BenefitSummaryReportWizard from "./BenefitSummaryReportWizard";
import BenefitWizard from "./BenefitWizard";
import GroupConfigurationSnapshotContext from "./GroupConfigurationSnapshotContext";
import ClientBenefitDetailReportWizard from "./ClientBenefitDetailReportWizard";
import CompareLookupDialog from "./CompareLookupDialog";
import { ECompareLookupDialogVariant, IDialogResult } from "./CompareLookupDialog/CompareLookupDialog";
import ActionItemsPanel from "./Controls/ActionItemsPanel";
import BenefitPanel from "./Controls/BenefitPanel";
import DataValidationPanel from "./Controls/DataValidationPanel";
import PlanPanel from "./Controls/PlanPanel";
import ProcessPanel from "./Controls/ProcessPanel";
import SnapshotDiscovery from "./Controls/SnapshotDiscovery";
import SystemDiagnosis from "./Controls/SystemDiagnosis";
import EnrollmentDetailDialog from "./EnrollmentDetailDialog";
import FeesAndPremiumCard from "./FeesAndPremiumCard";
import FeesAndPremiumWizard from "./FeesAndPremiumWizard";
import FeesGrid from './FeesGrid';
import FlexibleFieldView, { EScopeType } from './FlexibleFieldView';
import GroupConfigurationProperties from "./GroupConfigurationProperties";
import GroupStructureCESView from "./GroupStructure/GroupStructureCESView/GroupStructureCESView";
import GroupStructureDepartmentView from './GroupStructureDepartmentView';
import GroupStructureGroupNumberView from "./GroupStructureGroupNumberView";
import GroupStructureWizard, { EGroupStructureWizardVariant } from "./GroupStructureWizard";
import MonthlyRecurringFeesAgg from "./MonthlyRecurringFeesAgg";
import NonStandardTemplateWizard from "./NonStandardTemplateWizard";
import PlanComparisonReport from "./PlanComparisonReport";
import PlanWizard from "./PlanWizard";
import StopLossCard from "./StopLossCard";
import StopLossWizard from "./StopLossWizard";

interface IProps extends TComponentConnectedProps {
}

const GroupConfiguration = (props: IProps) => {
    const disabled = props.state.status.viewType !== EViewType.Modify;
    const { authorizations, principal } = useAuthentication();

    const theme = getTheme();
    const dispatch = useDispatch();

    const state = useSelector((state: RootState) => state);

    const application = useApplication();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);
    const params = useParams<RouterParams>();
    const location = useLocation<any>();

    const [isBSRHidden, setBSRHidden, isBSRMounted] = useHiddenWithMount(true);

    const [isPCRHidden, setPCRHidden, isPCRMounted] = useHiddenWithMount(true);

    const [groupConfigurationPropertiesMount, setGroupConfigurationPropertiesMount] = useState(false);
    const [groupConfigurationPropertiesIsOpen, setGroupConfigurationPropertiesIsOpen] = useState(false);

    const [showTemplateSuccessMessage, setShowTemplateSuccessMessage] = useState(false);
    const [templateMessage, setTemplateMessage] = useState<string | null>(null);

    const workspace = props.state.status.workspace;

    // const currentReleaseVersionStr = (state.data.config.bcbs_config_release.bcbs_versionnumber ?? '').match(/\d+/i)?.[0] ?? '0';
    // const currentReleaseVersion = parseInt(currentReleaseVersionStr);

    // const jsonReleaseVersionStr = ( state.data.json.releaseVersion ?? '').match(/\d+/i)?.[0] ?? '0';
    // const jsonReleaseVersion = parseInt(jsonReleaseVersionStr);

    // let correctReleaseVersion = false;
    // if (
    //     //This is to the right config has been released to handle non-standard templates
    //     (Constants.env === "System" && currentReleaseVersion >= 1101 && 1101 > jsonReleaseVersion) ||
    //     (Constants.env === "Production" && currentReleaseVersion >= 1102 && 1102 > jsonReleaseVersion)
    // ) {
    //     correctReleaseVersion = true;
    // }

    useEffect(() => {
        // on mount, tell menu what to display
        document.title = `Group Configuration | ${props.state.data.snapshot?.bcbs_name ?? '-'}`;

        // Check if navigated here after applying a template
        if (location.state?.templateApplied) {
            setShowTemplateSuccessMessage(true);

            // Clear the location state immediately to prevent showing on refresh
            window.history.replaceState({}, document.title);
        }

        // Check if navigated here after applying association template changes
        if (location.state?.associationTemplateMessage) {
            setTemplateMessage(location.state.associationTemplateMessage);
            setShowTemplateSuccessMessage(true);

            // Clear the location state immediately to prevent showing on refresh
            window.history.replaceState({}, document.title);
        }

    }, [props?.state?.data?.snapshot, location.state])

    useEffect(() => {
        //TODO windows animation frame/or postLayoutEffect hook if there is one => useCallback on windowAnimation frame I think
        if (history.location.search === '?navigation=pending')
            setTimeout(() => {
                let docElement: HTMLElement | null = null;

                if (params.groupNumberId) {
                    docElement = document.getElementById('groupstructure.groupnumber');
                }

                docElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

            }, 500);
    }, []);

    const runWithProcessing = useCallback(async (action: () => Promise<void>) => {
        try {
            dispatch(setProcessing(true));
            await action();
        } catch (error) {
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
        } finally {
            dispatch(setProcessing(false));
        }
    }, [dispatch]);

    const menu = useMemo(() => {

        const menu: ICommandBarItemProps[] = []

        // Group Configuration Properties
        if (!disabled)
            menu.push({
                key: 'Properties',
                text: 'Properties',
                iconProps: { iconName: 'Settings' },
                onClick: () => {
                    setGroupConfigurationPropertiesIsOpen(true);
                    setGroupConfigurationPropertiesMount(true);
                }
            });

        const compareItems: IContextualMenuItem[] = [];

        if (props.state.status.debug.admin || Constants.env !== 'Production')
            compareItems.push({
                key: 'compare.merge',
                text: 'Merge (Preview)',
                onClick: () => {
                    history.push(
                        AppLinkTo(props.state,
                            {
                                id: null,
                                type: 'merge',
                            }
                        )
                    );
                }
            });

        compareItems.push({
            key: 'compare.compare.preview',
            text: 'Compare',
            onClick: () => {
                history.push(
                    AppLinkTo(props.state,
                        {
                            id: null,
                            type: 'compare',
                        }
                    )
                );
            }
        });

        compareItems.push({
            key: 'compare.compare',
            text: 'Compare (Legacy)',
            onClick: () => {
                AppNavigateToExternal(history, props.state, ENavigationReason.COMPARE_APP);
            }
        });


        menu.push({
            key: 'compare',
            text: 'Compare',
            iconProps: { iconName: 'Compare' },
            subMenuProps: {
                items: compareItems,
            }
        });

        // Manage Templates
        if (!disabled)
            menu.push({
                key: 'template',
                text: 'Manage Templates',
                iconProps: { iconName: 'SyncOccurence' },
                subMenuProps: {
                    items: [
                        {
                            key: 'template.nonStandard',
                            text: 'Non-Standard Templates',
                            iconProps: { iconName: 'Document' },
                            onClick: () => {
                                history.push(AppLinkTo(props.state, { id: "nonstandardtemplatesScreen", type: 'nonstandardtemplates' }))
                            }
                        },
                        {
                            key: 'template.associationProduct',
                            text: 'Association Template',
                            iconProps: { iconName: 'Sync' },
                            onClick: () => {
                                history.push(`/records/blueRelationship/${props.state.data.snapshot._bcbs_groupid_value}/groupConfiguration/${props.state.data.snapshot.bcbs_groupconfigurationsnapshotid}/associationtemplates`);
                            }
                        }
                    ]
                }
            });

        function buildReportMenu() {
            const submenu: IContextualMenuItem[] = [];

            // CBDR 
            submenu.push({
                key: 'report.Client Benefit Detail Report',
                text: 'Client Benefit Detail Report',
                title: 'Generate Benefit Detail Report',
                iconProps: { iconName: 'Generate' },
                onClick: () => setCBDRWizardIsOpen(true)
            });

            // BDR 
            submenu.push({
                key: 'Benefit Detail Report',
                text: 'Benefit Detail Report',
                title: 'Generate Benefit Detail Report',
                iconProps: { iconName: 'Generate' },
                onClick: () => setBDRWizardIsOpen(true)
            });

            // NSE 
            submenu.push({
                key: 'report.Non-Standard Extract',
                text: 'Non-Standard Extract',
                title: 'Generate Non-Standard Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const excelExtractImport = await import("global/ExcelExtract")
                        const { NonstandardExcelGeneration } = excelExtractImport;
                        const result = await NonstandardExcelGeneration(props.state, dataSource)
                        const fileName = `NSE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    });
                }
            });

            // FPE
            submenu.push({
                key: 'Fees & Premiums Extract',
                text: 'Fees & Premiums Extract',
                title: 'Generate Fees & Premiums Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const excelExtract = await import("global/ExcelExtract")
                        const { FeesAndPremiumsExcelGeneration } = excelExtract;
                        const result = await FeesAndPremiumsExcelGeneration(props.state, dataSource);
                        const fileName = `FPE-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    });
                }
            });

            // CE
            submenu.push({
                key: 'report.Compare Extract',
                text: 'Compare Extract',
                title: 'Generate Compare Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    setCompareExtractMount(true);
                    setCompareExtractIsOpen(true);
                }
            });


            // ACE
            if (authorizations?.roles['BCBS - Power User'])
                submenu.push({
                    key: 'report.Adobe Campaign Extract',
                    text: 'Adobe Campaign Extract',
                    title: 'Generate Adobe Campaign Extract',
                    iconProps: { iconName: 'Generate' },
                    onClick: () => {
                        setAdobeCampaignExtractIsOpen(true);
                        setAdobeCampaignExtractMount(true);
                    }
                });

            // BDE
            if (authorizations?.roles['BCBS - Power User'])
                submenu.push({
                    key: 'report.Benefit Detail Extract',
                    text: 'Benefit Detail Extract',
                    title: 'Generate Benefit Detail Extract',
                    iconProps: { iconName: 'Generate' },
                    onClick: () => {
                        setBenefitDetailExtractIsOpen(true);
                        setBenefitDetailExtractMount(true);
                    }
                });

            // Pharmacy/ORE Extract
            submenu.push({
                key: 'report.Pharmacy/ORE Extract',
                text: 'Pharmacy/ORE Extract',
                title: 'Pharmacy/ORE Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const response = await dataSource.getOptumBulkExtractReports(props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid!);
                        saveAs(response.blob, response.fileName, { autoBom: true });
                    });
                }
            });

            // Pharmacy/Florida Prime Extract
            submenu.push({
                key: 'report.Pharmacy/PRE Extract',
                text: 'Pharmacy/Florida Prime Extract',
                title: 'Pharmacy/Florida Prime Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const response = await dataSource.getAsyncHttpStartGeneratePharmacyExtract({
                            bcbs_groupconfigurationsnapshotid: props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid!,
                            release: false,
                            isFloridaPrime: true,
                            isCapitalPrime: false,
                        });
                        saveAs(response.content, response.name, { autoBom: true });
                    });
                }
            });

            // Pharmacy/Florida Prime Extract
            submenu.push({
                key: 'report.Pharmacy/Capital Prime Extract',
                text: 'Pharmacy/Capital Prime Extract',
                title: 'Pharmacy/Capital Prime Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const response = await dataSource.getAsyncHttpStartGeneratePharmacyExtract({
                            bcbs_groupconfigurationsnapshotid: props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid!,
                            release: false,
                            isFloridaPrime: false,
                            isCapitalPrime: true,
                        });
                        saveAs(response.content, response.name, { autoBom: true });
                    });
                }
            });

            // NSE
            submenu.push({
                key: 'report.Financial Extract',
                text: 'Financial Extract',
                title: 'Financial Extract',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const excelExtractImport = await import("global/ExcelExtract")
                        const { PFABExtract } = excelExtractImport;
                        const result = await PFABExtract(props.state, dataSource)
                        const fileName = `Financial-${props.state.data.snapshot?.bcbs_name}(${props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid})-${new Date().getTime()}.xlsx`;
                        saveAs(result.file, fileName);
                    });
                }
            });

            //Schedule A
            submenu.push({
                key: 'report.Schedule A',
                text: 'Schedule A',
                title: 'Generate Schedule A',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    void runWithProcessing(async () => {
                        const ScheduleAActionImport = await import('global/Application/ScheduleAGenerateAction');
                        const { ScheduleAGenerateAction } = ScheduleAActionImport;
                        dispatch(ScheduleAGenerateAction(dataSource));
                    });
                }
            });

            if (Constants.env !== 'Production' || Constants.app.isLocalHost)
                submenu.push({
                    key: 'report.Benefit Summary Report',
                    text: 'Benefit Summary Report',
                    title: 'Benefit Summary Report',
                    iconProps: { iconName: 'Generate' },
                    onClick: () => {
                        setBSRHidden(false);
                    }
                });

            submenu.push({
                key: 'report.Plan Comparison Report',
                text: 'Plan Comparison Report',
                title: 'Plan Comparison Report',
                iconProps: { iconName: 'Generate' },
                onClick: () => {
                    setPCRHidden(false);
                }
            });

            if (submenu.length > 0)
                return ({
                    key: 'report',
                    text: 'Reports/Extracts',
                    iconProps: { iconName: 'Generate' },
                    subMenuProps: {
                        items: submenu
                    }
                } as ICommandBarItemProps);

            return null;
        };

        const reportMenu = buildReportMenu();

        if (reportMenu)
            menu.push(reportMenu);

        // Enrollment Details
        if (disabled)
            menu.push({
                key: 'main.groupConfigurationEnrollmentDetail',
                text: 'Enrollment Details',
                iconProps: { iconName: 'OpenEnrollment' },
                onClick: () => {
                    setEnrollmentDetailIsOpen(true);
                    setEnrollmentDetailMount(true);
                }
            });

        const pharmacySubmenuItems: IContextualMenuItem[] = [];

        const pharmacySubmenu: ICommandBarItemProps = {
            key: 'pharmacy',
            text: 'Pharmacy',
            iconProps: { iconName: 'Pill' },
            subMenuProps: {
                items: pharmacySubmenuItems
            }
        }
        menu.push(pharmacySubmenu);

        pharmacySubmenuItems.push({
            key: 'pharmacy.ore',
            text: 'OptumRx',
            iconProps: { iconName: 'Pill' },
            onClick: () => {
                const foundSnapshot = compareUnderWritingReviewToWithoutDispatch(props.state, props.state.data.snapshot);
                // TODO move this to AppNavigateToExternal
                history.push(`/pharmacyLanding/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/${foundSnapshot?.bcbs_groupconfigurationsnapshotid ?? ""}`)
                // (window as Window).location = (`/pharmacyLanding/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}/${foundSnapshot?.bcbs_groupconfigurationsnapshotid ?? ""}`);
            }
        });

        pharmacySubmenuItems.push({
            key: 'pharmacy.prime.florida',
            text: 'Florida Prime Rx',
            iconProps: { iconName: 'Pill' },
            onClick: () => {
                history.push(AppLinkTo(props.state, { type: 'floridaPrimeRx', id: "" }))
            }
        });


        pharmacySubmenuItems.push({
            key: 'pharmacy.prime.capital',
            text: 'Capital Prime Rx',
            iconProps: { iconName: 'Pill' },
            onClick: () => {
                history.push(AppLinkTo(props.state, { type: 'capitalPrimeRx', id: "" }))
            }
        });


        menu.push()

        if (
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value ||
            props.state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value ||
            props.state.status.debug.admin
        )
            menu.push({
                key: 'enmChecklist',
                text: 'E&M Checklist',
                iconProps: { iconName: 'CheckList' },
                onClick: () => {
                    history.push(AppLinkTo(props.state, { id: '', type: 'checkList' }))
                }
            });


        menu.push({
            key: 'subgroup',
            text: 'Subgroup Information',
            iconProps: { iconName: 'ViewListGroup' },
            onClick: () => { history.push(`/subgroupInformation/${state.data.snapshot._bcbs_groupid_value}/${state.data.snapshot.bcbs_groupconfigurationsnapshotid}`) }
        });

        return { items: menu, key: 'groupConfiguration' };
    }, [props.state.status]);

    const account = props.state.status?.account;


    const [benefitWizardIsOpen, { setTrue: benefitWizardOpen, setFalse: benefitWizardClose }] = useBoolean(false);
    const [planWizardIsOpen, { setTrue: planWizardOpen, setFalse: planWizardClose }] = useBoolean(false);
    const [nonStandardTemplateWizardIsOpen, { setTrue: nonStandardTemplateWizardOpen, setFalse: nonStandardTemplateWizardClose }] = useBoolean(false);
    const [feesAndPremiumWizardIsOpen, { setTrue: feesAndPremiumWizardOpen, setFalse: feesAndPremiumWizardClose }] = useBoolean(false);

    const [groupStructureWizardSpecial, setGroupStructureWizardSpecial] = useState<EGroupStructureWizardVariant | null>(null);

    const [stopLossWizardIsOpen, { setTrue: stopLossWizardOpen, setFalse: stopLossWizardClose }] = useBoolean(false);

    const [ARWizardMount, setARWizardMount] = useState(false);
    const [ARWizardIsOpen, setARWizardIsOpen] = useState(false);

    const [CBDRWizardIsOpen, setCBDRWizardIsOpen] = useState(false);

    const [BDRWizardIsOpen, setBDRWizardIsOpen] = useState(false);

    const [enrollmentDetailMount, setEnrollmentDetailMount] = useState(false);
    const [enrollmentDetailIsOpen, setEnrollmentDetailIsOpen] = useState(false);

    const [nonStandardTemplateMount, setNonStandardTemplateMount] = useState(false);
    const [nonStandardTemplateIsOpen, setNonStandardTemplateIsOpen] = useState(false);

    const [compareExtractMount, setCompareExtractMount] = useState(false);
    const [compareExtractIsOpen, setCompareExtractIsOpen] = useState(false);

    const [adobeCampaignExtractMount, setAdobeCampaignExtractMount] = useState(false);
    const [adobeCampaignExtractIsOpen, setAdobeCampaignExtractIsOpen] = useState(false);


    const [benefitDetailExtractMount, setBenefitDetailExtractMount] = useState(false);
    const [benefitDetailExtractIsOpen, setBenefitDetailExtractIsOpen] = useState(false);

    const history = useHistory();

    const categoryGroupConfiguration = props.state.data.config?.cache?.map_bcbs_config_category_named["Group Configuration"];

    const groupStructureMenu: IContextualMenuItem[] = [];

    const scopeVisibility = BuildVisibilityScope(
        null,
        props.state.data.json,
        null,
        null
    );

    if (!disabled)
        groupStructureMenu.push(
            {
                key: 'groupConfiguration.groupStructure.manage',
                name: 'Manage',
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    setGroupStructureWizardSpecial(EGroupStructureWizardVariant.wizard);
                },
            },

        );

    if (disabled)
        groupStructureMenu.push(
            {
                key: 'groupConfiguration.groupStructure.export',
                name: 'Report',
                icon: 'DocumentManagement',
                onClick: (event, item) => {
                    if (event?.stopPropagation)
                        event.stopPropagation();

                    setGroupStructureWizardSpecial(EGroupStructureWizardVariant.export);
                },
            },

        );

    function renderPanels() {
        return <>
            {groupConfigurationPropertiesMount &&
                <GroupConfigurationProperties
                    isOpen={groupConfigurationPropertiesIsOpen}
                    onDismiss={() => setGroupConfigurationPropertiesIsOpen(false)}
                    onConfirm={(e) => {
                        setGroupConfigurationPropertiesIsOpen(false);

                        if (e?.value)
                            dispatch(addUpdateGroupConfiguration(e.value));

                    }}
                    defaultValue={props.state.data.json}
                    variant={"change"}
                />
            }

            <PlanWizard
                isOpen={planWizardIsOpen || !!groupStructureWizardSpecial}
                onDismiss={planWizardClose}
                onCancel={planWizardClose}
                variant="edit"
            />
            <BenefitWizard
                isOpen={benefitWizardIsOpen}
                onCancel={benefitWizardClose}
                onDismiss={benefitWizardClose}
            />
            <FeesAndPremiumWizard
                isOpen={feesAndPremiumWizardIsOpen}
                onCancel={feesAndPremiumWizardClose}
                onDismiss={feesAndPremiumWizardClose}
                variant={'add'}
            />
            <GroupStructureWizard
                isOpen={!!groupStructureWizardSpecial}
                onCancel={() => setGroupStructureWizardSpecial(null)}
                onDismiss={() => setGroupStructureWizardSpecial(null)}
                variant={groupStructureWizardSpecial}
            />
            <ClientBenefitDetailReportWizard
                isOpen={CBDRWizardIsOpen}
                variant={'CBDR'}
                onCancel={() => setCBDRWizardIsOpen(false)}
                onDismiss={() => setCBDRWizardIsOpen(false)}
            />
            <ClientBenefitDetailReportWizard
                isOpen={BDRWizardIsOpen}
                variant={'BDR'}
                onCancel={() => setBDRWizardIsOpen(false)}
                onDismiss={() => setBDRWizardIsOpen(false)}
            />
            <StopLossWizard
                isOpen={stopLossWizardIsOpen}
                onDismiss={stopLossWizardClose}
                onCancel={stopLossWizardClose}
                variant={"add"}
                onConfirm={(event) => {
                    const t: IAddFeesPayload[] = [
                        {
                            stopLoss: event?.value.stopLossSelected!,
                        }
                    ];

                    dispatch(addUpdateStopLoss(t));
                    stopLossWizardClose();
                }}
            />

            {ARWizardMount &&
                <AccountsReceivableWizard
                    isOpen={ARWizardIsOpen}
                    onDismiss={() => setARWizardIsOpen(false)}
                    onCancel={() => setARWizardIsOpen(false)}
                    variant="edit"
                />
            }

            {enrollmentDetailMount &&
                <EnrollmentDetailDialog
                    isOpen={enrollmentDetailIsOpen}
                    onDismiss={() => { setEnrollmentDetailIsOpen(false); }}
                    onConfirm={() => { setEnrollmentDetailIsOpen(false); }}
                />
            }

            {compareExtractMount &&
                <CompareLookupDialog
                    variant={ECompareLookupDialogVariant.CompareExtractDialog}
                    isOpen={compareExtractIsOpen}
                    onDismiss={() => { setCompareExtractIsOpen(false) }}
                    onContinue={async (viewModel) => {

                        const result: IDialogResult = {
                            dismiss: true,
                        };

                        if (!viewModel.leftSnapshotRef?.id)
                            return result;

                        if (!viewModel.rightSnapshotRef?.id)
                            return result;

                        await (void runWithProcessing(async () => {

                            const response = await dataSource.getSnapshotCompareExtract(
                                {
                                    bcbs_groupconfigurationsnapshotid: viewModel.leftSnapshotRef?.id!,
                                    blobVersion: viewModel.leftBlobVersion ?? null
                                },
                                {
                                    bcbs_groupconfigurationsnapshotid: viewModel.rightSnapshotRef?.id!,
                                    blobVersion: viewModel.rightBlobVersion ?? null
                                },
                                viewModel.generateSingleSheet,
                                viewModel.underwritingReviewNecessary,
                                viewModel.affectsIDCards,
                                viewModel.targetedClaims,
                                viewModel.useReleaseID,

                                () => {
                                    // TODO implement a progress status
                                    console.log(Date.now())
                                },
                            );

                            saveAs(response.blob, response.fileName);

                            result.dismiss = true;
                        }));

                        if (result.dismiss) {
                            setCompareExtractIsOpen(false);
                        }

                        return result;

                    }}
                />
            }

            {nonStandardTemplateMount &&
                <NonStandardTemplateWizard
                    isOpen={nonStandardTemplateIsOpen}
                    onCancel={() => setNonStandardTemplateIsOpen(false)}
                    onDismiss={() => { setNonStandardTemplateIsOpen(false) }}
                />
            }

            {adobeCampaignExtractMount &&
                <AdobeCampaignExtract
                    isOpen={adobeCampaignExtractIsOpen}
                    onDismiss={() => { setAdobeCampaignExtractIsOpen(false) }}
                />
            }

            {benefitDetailExtractMount &&
                <BenefitDetailExtract
                    isOpen={benefitDetailExtractIsOpen}
                    onDismiss={() => { setBenefitDetailExtractIsOpen(false) }}
                />
            }

            {isBSRMounted &&
                <BenefitSummaryReportWizard
                    isOpen={!isBSRHidden}
                    onDismiss={() => {
                        setBSRHidden(true);
                    }}
                    onCancel={() => {
                        setBSRHidden(true);
                    }}
                />
            }


            {isPCRMounted &&
                <PlanComparisonReport
                    isOpen={!isPCRHidden}
                    onDismiss={() => {
                        setPCRHidden(true);
                    }}
                    onCancel={() => {
                        setPCRHidden(true);
                    }}
                />
            }

        </>;
    }

    function renderSummary() {
        return <CustomPanel title={"Summary"}
            storageId={"groupConfiguration.summary"}
            canCollapse={true}
            defaultCollapsed={true}
            mountWhenExpanded={true}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignContent: 'stretch' }}>
                {account &&
                    <AccountSummary data={props.state.data} />
                }

                {props?.state?.data?.snapshot &&
                    <SnapshotSummary data={props.state.data} />
                }
            </div>
        </CustomPanel>;
    }

    function renderAttributes() {
        if (!props.state.data?.json?.fields)
            return <></>;

        return <CustomPanel title={"Attributes"}
            canCollapse={true}
            defaultCollapsed={params.tabId ? false : true}
            storageId={"groupConfiguration.attributes"}
            mountWhenExpanded={true}
        >
            <FlexibleFieldView
                disabled={disabled}
                fields={props.state.data.json.fields}
                tiers={[]}
                category={categoryGroupConfiguration}
                scope={props.state.data.json}
                visibility={BuildVisibilityScope(
                    null,
                    props.state.data.json,
                    null,
                    null
                )}
                cancellation={
                    props?.state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus["Cancelled"] ||
                    props?.state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus["Cancellation in Progress"] ||
                    props?.state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus["Transferred to Other LOB"] ||
                    props?.state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus["Transferred to Other LOB in Progress"]
                }
                scopeType={EScopeType.groupConfiguration}
            />
        </CustomPanel>;
    }

    function renderPlan() {
        return <PlanPanel
            onAdd={planWizardOpen}
        />;
    }

    function renderBenefit() {
        return <BenefitPanel
            onAdd={benefitWizardOpen}
        />;
    }

    function renderGroupStructure() {
        return <CustomPanel
            title={"Group Structure"}
            id={"groupStructure"}
            canCollapse={true}
            defaultCollapsed={params.groupNumberId ? false : true}
            storageId={"groupConfiguration.groupStructure"}
            items={groupStructureMenu}
            mountWhenExpanded={true}
        >
            <GroupStructureCESView disabled={disabled} />
            <GroupStructureGroupNumberView
                disabled={disabled}
                visibility={scopeVisibility}
            />
            <GroupStructureDepartmentView disabled={disabled} />
        </CustomPanel>;
    }

    function renderFeesAndPremium() {
        return <CustomPanel title={"Fees & Premiums"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.feesAndPremiums"}
            mountWhenExpanded={true}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >
                {props.state?.data.json?.feesAndPremiums?.map(eachFeesAndPremium => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if (eachFeesAndPremium.deactivated !== props.state.data.json.showInActives?.value)
                        return <></>;

                    return (
                        <FeesAndPremiumCard
                            key={eachFeesAndPremium.id}
                            contextPlans={props.state.data.json.plans}
                            feesAndPremium={eachFeesAndPremium}
                            state={props.state}
                            onClick={() =>
                                history.push(AppLinkTo(props.state, { id: eachFeesAndPremium.id, type: 'feesAndPremium' }))
                            }
                        />
                    );
                })}

                {!disabled && application.ChangeTypePermissions.feesAndPremium.add &&
                    <SimpleCard
                        key={"defaultFeesAndPremiums"}
                        onClick={() => feesAndPremiumWizardOpen()}
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                    />}
            </Stack>

            <FeesGrid
                disabled={disabled}
                creditHidden={true}
                visibility={scopeVisibility}
            />

            <MonthlyRecurringFeesAgg />

        </CustomPanel>;
    }

    function renderStopLoss() {

        const planStopLoss: FAM_IStopLoss[] = props.state.data?.json?.stopLoss ?? [];

        return <CustomPanel title={"Stop Loss"}
            canCollapse={true}
            defaultCollapsed={true}
            contentStyle={{ overflow: 'visible' }}
            storageId={"groupConfiguration.stopLoss"}
            mountWhenExpanded={true}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >
                {planStopLoss.map(eachStopLoss => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if (eachStopLoss.deactivated !== props.state.data.json.showInActives?.value)
                        return <></>;

                    return (
                        <StopLossCard
                            key={eachStopLoss.id}
                            stopLoss={eachStopLoss}
                            onClick={() => history.push(AppLinkTo(props.state, { id: eachStopLoss.id, type: 'stopLoss' }))}
                        />
                    );
                })}
                {!disabled && application.ChangeTypePermissions.stopLoss.add &&
                    <SimpleCard
                        key={"defaultStopLoss"}
                        title="Add"
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                        onClick={(e) => {
                            stopLossWizardOpen();
                        }}
                    />
                }
            </Stack>
        </CustomPanel>;
    }

    function renderAccountsReceivable() {

        const claimsAR: FAM_IAccountsReceivable[] = props.state.data?.json?.claimsARs ?? [];

        return <CustomPanel title={"Accounts Receivable"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.accountsReceivable"}
            mountWhenExpanded={true}
        >
            <Stack
                horizontal
                wrap
                verticalFill
                tokens={{ childrenGap: 15 }}
                horizontalAlign="start"
                style={{ padding: '15px' }}
            >

                {claimsAR.map(eachAccountsReceivable => {

                    // skip deactivated records/only show deactivated records if permitted at group config properties level
                    if (eachAccountsReceivable.deactivated !== props.state.data.json.showInActives?.value)
                        return <></>;

                    return <AccountsReceivableCard
                        key={eachAccountsReceivable.id}
                        accountsReceivable={eachAccountsReceivable}
                        description={eachAccountsReceivable.name}
                        onClick={() => history.push(AppLinkTo(props.state, { id: eachAccountsReceivable.id, type: 'claimsAR' }))}
                    />;

                })}
                {!disabled && application.ChangeTypePermissions.canAddAccountReceivable &&
                    <SimpleCard
                        key={"defaultAccountsReceivable"}
                        title="Add"
                        onRenderFront={() => {
                            return (
                                <>
                                    <p><Text>Add</Text></p>
                                    <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                                </>
                            );
                        }}
                        onClick={(e) => {
                            setARWizardIsOpen(true);
                            setARWizardMount(true);
                        }}
                    />
                }
            </Stack>
        </CustomPanel>;
    }

    function renderActionItemsPanel() {
        return <ActionItemsPanel />;
    }

    function renderDiagnosisPanel() {
        if (!props.state.status.debug.diagnosis)
            return <></>;

        return <CustomPanel title={"Diagnosis"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.diagnosis"}
            mountWhenExpanded={true}
        >
            <Stack style={{ marginTop: '15px' }}>
                <ProcessPanel />
                <DataValidationPanel />
                <SnapshotDiscovery />
                <SystemDiagnosis />
            </Stack>
        </CustomPanel>;
    }

    return <GroupConfigurationSnapshotContext contextMenu={menu}>
        {renderPanels()}
        {renderActionItemsPanel()}
        {renderDiagnosisPanel()}
        {renderSummary()}
        {renderAttributes()}
        {renderPlan()}
        {renderBenefit()}
        {renderAccountsReceivable()}
        {renderGroupStructure()}
        {renderFeesAndPremium()}
        {renderStopLoss()}
    </GroupConfigurationSnapshotContext>;
}



function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(GroupConfiguration)
